import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import MessageSidebar from '../components/MessageSidebar';
import ChatThread from '../components/ChatThread';

const API = '/api';

const Messages = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('active');
  const [selectedConv, setSelectedConv] = useState(null);
  const [showSidebar, setShowSidebar] = useState(true);

  // Auto-select conversation from URL param
  useEffect(() => {
    const convId = searchParams.get('conversation');
    if (convId && !selectedConv) {
      const fetchConv = async () => {
        try {
          const token = localStorage.getItem('token');
          // Try to find the conversation in all statuses
          for (const status of ['active', 'request', 'closed']) {
            const res = await axios.get(`${API}/messages/conversations?status=${status}&limit=50`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const found = (Array.isArray(res.data) ? res.data : []).find((c) => c.id === convId);
            if (found) {
              setSelectedConv(found);
              setActiveTab(status);
              setShowSidebar(false);
              return;
            }
          }
        } catch { /* */ }
      };
      fetchConv();
    }
  }, [searchParams, selectedConv]);

  const handleSelectConversation = (conv) => {
    setSelectedConv(conv);
    setShowSidebar(false);
    // Update URL without full navigation
    window.history.replaceState(null, '', `/messages?conversation=${conv.id}`);
  };

  const handleBack = () => {
    setSelectedConv(null);
    setShowSidebar(true);
    window.history.replaceState(null, '', '/messages');
  };

  const handleConversationUpdate = () => {
    // Refresh sidebar
    if (window.__refreshMessageSidebar) window.__refreshMessageSidebar();
  };

  const isCreator = user && (user.is_creator === true || user.role === 'creator' || user.role === 'artist' || user.role === 'author');
  const isAdmin = user && (user.role === 'admin' || user.is_admin === true);

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-zinc-950">
        <div className="text-center">
          <p className="text-zinc-400 mb-4">Please sign in to view messages</p>
          <button onClick={() => navigate('/auth')} className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">
            Sign In
          </button>
        </div>
      </div>
    );
  }

  if (!isCreator && !isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-zinc-950">
        <div className="text-center">
          <p className="text-zinc-400">Direct messaging is available for creators only.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 top-[65px] flex bg-zinc-950 z-30" data-testid="messages-page">
      {/* Sidebar - always visible on desktop, toggleable on mobile */}
      <div className={`w-full md:w-[340px] md:min-w-[340px] flex-shrink-0 ${showSidebar ? 'block' : 'hidden md:block'}`}>
        <MessageSidebar
          activeTab={activeTab}
          onTabChange={(tab) => { setActiveTab(tab); setSelectedConv(null); }}
          selectedConvId={selectedConv?.id}
          onSelectConversation={handleSelectConversation}
          userId={user.id}
        />
      </div>

      {/* Chat thread - always visible on desktop, toggleable on mobile */}
      <div className={`flex-1 min-w-0 ${!showSidebar ? 'block' : 'hidden md:block'}`}>
        <ChatThread
          conversation={selectedConv}
          onBack={handleBack}
          onConversationUpdate={handleConversationUpdate}
        />
      </div>
    </div>
  );
};

export default Messages;
